interface FetchAllReportDataParams<T> {
  total: number;
  batchSize: number;
  concurrency?: number;
  maxRetries?: number;
  fetchBatch: (params: { skip: number; limit: number }) => Promise<T[]>;
  onProgress?: (progress: number) => void;
}

export async function fetchAllReportData<T>({
  total,
  batchSize,
  concurrency = 5,
  maxRetries = 3,
  fetchBatch,
  onProgress,
}: FetchAllReportDataParams<T>): Promise<T[]> {
  if (total <= 0 || batchSize <= 0) return [];

  const skips: number[] = [];
  for (let skip = 0; skip < total; skip += batchSize) {
    skips.push(skip);
  }

  const totalBatches = skips.length;
  const results: T[][] = new Array(totalBatches);
  let completedBatches = 0;

  const fetchWithRetry = async (skip: number): Promise<T[]> => {
    let attempt = 0;

    while (attempt <= maxRetries) {
      try {
        return await fetchBatch({ skip, limit: batchSize });
      } catch (error) {
        attempt += 1;
        if (attempt > maxRetries) throw error;
        await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
      }
    }

    throw new Error("Unreachable: retry loop exited without resolving");
  };

  for (let i = 0; i < totalBatches; i += concurrency) {
    const chunk = skips.slice(i, i + concurrency);

    await Promise.all(
      chunk.map(async (skip, chunkIndex) => {
        results[i + chunkIndex] = await fetchWithRetry(skip);
        completedBatches += 1;
        onProgress?.(
          Number(
            Math.min((completedBatches / totalBatches) * 100, 100).toFixed(0),
          ),
        );
      }),
    );
  }

  return results.flat();
}
